// src/pages/hr/Payroll.jsx
// Bug 07 fix: HR manager ab payroll batches dekh sakta hai aur review bhi kar sakta hai.
// Bug 08 fix: Year input mein min/max limit add ki — pehle year 384939 tak ja raha tha.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banknote, Loader2, Plus, FileText,
  Download, X
} from 'lucide-react';
import { payrollService } from '../../services/payrollService';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import StatusBadge from '../../components/StatusBadge';

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();

const Payroll = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notify } = useNotification();
  const [activeTab, setActiveTab] = useState('batches');
  const [batches, setBatches] = useState([]);
  const [myPayslips, setMyPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRunModal, setShowRunModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Bug 07 fix: HR bhi admin hai — batches tab dikhao
  const isAdmin = user && ['super_admin', 'hr_manager', 'finance_manager'].includes(user.role);
  const canRunPayroll = user && ['super_admin', 'hr_manager'].includes(user.role);
  const canReview = user && ['super_admin', 'finance_manager', 'hr_manager'].includes(user.role);
  const canApprove = user && user.role === 'super_admin';

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'batches' && isAdmin) {
        const res = await payrollService.getBatches();
        setBatches(res.data || []);
      } else {
        const res = await payrollService.getMyPayslips();
        setMyPayslips(res.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [activeTab]);

  const handleRunPayroll = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData(e.target);
    const year = parseInt(formData.get('year'));
    // Bug 08 fix: year validation
    if (year < 2020 || year > 2099) {
      notify('Please enter a valid year (2020 - 2099)', 'error');
      setSubmitting(false);
      return;
    }
    try {
      const res = await payrollService.runPayroll(parseInt(formData.get('month')), year);
      const batch = res.data || res;
      setShowRunModal(false);
      loadData();
      if (batch && batch.skipped_no_salary && batch.skipped_no_salary.length > 0) {
        const names = batch.skipped_no_salary.map((s) => s.full_name + ' (' + s.employee_id + ')').join('\n');
        notify(
          'Payroll run complete for ' + batch.total_employees + ' employee(s).\n\n' +
          batch.skipped_no_salary.length + ' active employee(s) were skipped because they have no salary structure set:\n' + names,
          'info',
          10000
        );
      } else {
        notify('Payroll run complete for ' + batch.total_employees + ' employee(s).', 'success');
      }
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (id, type) => {
    try {
      if (type === 'review') await payrollService.reviewBatch(id);
      if (type === 'approve') await payrollService.approveBatch(id);
      loadData();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payroll & Salaries</h1>
          <p className="text-sm text-gray-500 font-medium">Manage monthly salary disbursements and payslips</p>
        </div>
        {canRunPayroll && activeTab === 'batches' && (
          <button
            onClick={() => setShowRunModal(true)}
            className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Run Monthly Payroll
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-100">
        {isAdmin && (
          <button
            onClick={() => setActiveTab('batches')}
            className={'px-6 py-3 text-sm font-semibold border-b-2 transition-all ' + (activeTab === 'batches' ? 'border-primary text-primary' : 'border-transparent text-gray-400')}
          >
            Payroll Batches
          </button>
        )}
        <button
          onClick={() => setActiveTab('payslips')}
          className={'px-6 py-3 text-sm font-semibold border-b-2 transition-all ' + (activeTab === 'payslips' ? 'border-primary text-primary' : 'border-transparent text-gray-400')}
        >
          My Payslips
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-primary opacity-20 w-10 h-10" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {activeTab === 'batches' ? (
            batches.length === 0 ? (
              <div className="bg-white p-12 text-center rounded-card border border-dashed text-gray-400">
                No payroll batches found.
              </div>
            ) : (
              batches.map((b) => (
                <div
                  key={b.id}
                  onClick={() => navigate('/payroll/' + b.batch_id)}
                  className="bg-white p-6 rounded-card border border-gray-100 shadow-soft flex items-center justify-between gap-4 cursor-pointer hover:border-primary/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-50 rounded-xl text-primary">
                      <Banknote className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{MONTHS[b.month - 1] + ' ' + b.year}</h3>
                      <p className="text-xs text-gray-500 font-medium">Batch ID: {b.batch_id} &bull; {b.total_employees} Employees</p>
                      <div className="mt-1"><StatusBadge status={String(b.status)} /></div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Total Net Pay</p>
                      <p className="text-sm font-black text-gray-900">{b.total_net.toLocaleString()} PKR</p>
                    </div>

                    <div className="flex gap-2">
                      {/* Bug 07 fix: HR aur Finance dono review kar saken */}
                      {canReview && b.status === 'Pending Review' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleAction(b.batch_id, 'review'); }}
                          className="bg-primary text-white px-4 py-2 rounded-lg text-xs font-bold uppercase"
                        >
                          Submit for Approval
                        </button>
                      )}
                      {canApprove && b.status === 'Pending Approval' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleAction(b.batch_id, 'approve'); }}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg text-xs font-bold uppercase"
                        >
                          Approve & Pay
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )
          ) : (
            myPayslips.length === 0 ? (
              <div className="bg-white p-12 text-center rounded-card border border-dashed text-gray-400">
                Your payslips will appear here after payroll is processed.
              </div>
            ) : (
              myPayslips.map((p) => (
                <div key={p.id} className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-50 rounded-xl text-gray-400">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{MONTHS[p.month - 1] + ' ' + p.year}</h3>
                      <p className="text-xs text-gray-500 font-medium tracking-tight">
                        Generated on {new Date(p.generated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await payrollService.viewPayslip(p.payslip_id);
                      } catch (err) {
                        alert(err.message || 'Failed to load payslip');
                      }
                    }}
                    className="p-2.5 text-primary hover:bg-primary/5 border border-primary/10 rounded-xl transition-all"
                    title="Download Payslip"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </div>
              ))
            )
          )}
        </div>
      )}

      {/* Run Payroll Modal */}
      {showRunModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900">
              Run New Payroll
              <button onClick={() => setShowRunModal(false)}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleRunPayroll} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Select Month</label>
                <select
                  name="month"
                  required
                  defaultValue={new Date().getMonth() + 1}
                  className="w-full p-2.5 border rounded-xl text-sm bg-gray-50"
                >
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">
                  Year
                </label>
                {/* Bug 08 fix: min aur max add kiya — infinite year scroll band */}
                <input
                  name="year"
                  type="number"
                  required
                  defaultValue={currentYear}
                  min={2020}
                  max={2099}
                  className="w-full p-2.5 border rounded-xl text-sm bg-gray-50"
                />
                <p className="text-[10px] text-gray-400 mt-1">Valid range: 2020 — 2099</p>
              </div>
              <p className="text-[10px] text-gray-500 italic bg-amber-50 p-2.5 rounded-lg border border-amber-100">
                This will compile basic salaries, approved commissions, and deductions for all active employees.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Initiate Batch
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payroll;